import { token } from '@atlaskit/tokens';
import { readableTextColor as baseReadableTextColor } from '@atlassian/color-picker/color-utils';

import type { NavigationStyle } from '../controllers/navigation-style-context';
import type { NavigationBarApiResponse } from '../types';

import { HUB_COLOR_CSS_VARS } from './hub-brand-palette';

const readableTextColor = (color: string): string =>
	baseReadableTextColor(color, '@atlassian/company-hub-common');

export const DEFAULT_NAVIGATION_OPTIONS: NavigationStyle = {
	padding: true,
	fixedAtTop: false,
	backgroundColor: '#FFFFFF',
	textColor: '#172B4D',
	borderColor: '#DFE1E6',
	borderType: 'NO_BORDER',
	cornerRadius: 8,
};

/**
 * Returns the default navigation options resolved against a brand color.
 *
 * Under advanced theming the background defaults to the brand's darkest palette
 * shade (`primary.1000`) as a CSS-var reference. The `<HubThemingProvider>`
 * wrapper emits `--hub-color-primary-1000` and re-emits it whenever the global
 * primary color changes, so the navbar tracks the brand via the CSS cascade —
 * no React re-derivation needed, and persisting this value on publish is
 * harmless because it keeps tracking. Text defaults to the readable light
 * token: `primary.1000` is always the darkest ramp slot, so light text is
 * always the WCAG-readable choice (`brandColor` is irrelevant here — the
 * var-ref, not the raw hex, drives the color).
 *
 * When advanced theming is off, a provided brand color is used as the default
 * background with an auto-derived contrasting text color (legacy behaviour);
 * otherwise it falls back to the hardcoded white/dark defaults.
 */
export function getDefaultNavigationOptions(
	brandColor?: string,
	isAdvancedThemingEnabled?: boolean,
): NavigationStyle {
	if (isAdvancedThemingEnabled) {
		return {
			...DEFAULT_NAVIGATION_OPTIONS,
			backgroundColor: `var(${HUB_COLOR_CSS_VARS['primary.1000']})`,
			textColor: token('color.text.inverse'),
		};
	}
	if (!brandColor) {
		return DEFAULT_NAVIGATION_OPTIONS;
	}
	return {
		...DEFAULT_NAVIGATION_OPTIONS,
		backgroundColor: brandColor,
		textColor: readableTextColor(brandColor),
	};
}

export function mapApiToNavigationOptions(
	apiData: NavigationBarApiResponse,
	brandColor?: string,
	isCornerRadiusEnabled?: boolean,
	isAdvancedThemingEnabled?: boolean,
): NavigationStyle {
	const defaults = getDefaultNavigationOptions(brandColor, isAdvancedThemingEnabled);

	if (!apiData) {
		return defaults;
	}

	return {
		padding: apiData.padding ?? defaults.padding,
		fixedAtTop: apiData.positionType === 'FIXED',
		backgroundColor: apiData.backgroundColor ?? defaults.backgroundColor,
		textColor: apiData.textColor ?? defaults.textColor,
		borderColor: apiData.borderColor ?? defaults.borderColor,
		borderType:
			apiData.borderType === 'BOTTOM_BORDER'
				? 'BOTTOM_BORDER'
				: apiData.borderType === 'ALL_BORDER'
					? 'ALL_BORDER'
					: 'NO_BORDER',
		// When the corner-radius feature is on, preserve a missing value as null so
		// the navbar inherits --hub-corner-radius. Otherwise keep the legacy 8 default.
		cornerRadius: isCornerRadiusEnabled
			? (apiData.cornerRadius ?? null)
			: (apiData.cornerRadius ?? defaults.cornerRadius),
	};
}
